import { toast } from "@relex-solutions/fragments"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createElement } from "react"
import { useTranslation } from "react-i18next"
import { AuthorizationError } from "@/shared/api/errors/AuthorizationError"
import { CUSTOMERS_BASE_PATH } from "@/shared/constants/routePaths"
import { useCmApi } from "@/shared/hooks/useCmApi"

interface UpdateParentEdgeParams {
  selectedNodeId: string
  selectedNodeName: string
  newParentId: string | null
  oldParentId: string | null | undefined
  newParentName?: string
}

/**
 * Hook for updating parent node relationship
 */
export const useUpdateParentEdge = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const cmApi = useCmApi()

  const mutation = useMutation({
    mutationFn: async ({ selectedNodeId, newParentId, oldParentId }: UpdateParentEdgeParams) => {
      if (newParentId && oldParentId) {
        await cmApi.updatePlanCustomerParent(selectedNodeId, newParentId, oldParentId)
      }
    },
    onSuccess: async (_, { selectedNodeName, newParentId, newParentName }) => {
      // Explicitly refetch and wait for completion to ensure graph reflects changes
      await Promise.allSettled([
        queryClient.refetchQueries({ queryKey: ["relatedCustomers"] }),
        queryClient.refetchQueries({ queryKey: ["customerNeighbors"] })
      ])

      if (newParentId && newParentName) {
        const mainMessage = t("visualization.toast.parentUpdated.message", {
          customerName: selectedNodeName,
          parentName: newParentName
        })
        const planResourcesUrl = `${CUSTOMERS_BASE_PATH}/${newParentId}/plan-resources`

        const bodyContent = createElement(
          "div",
          null,
          `${t("visualization.toast.parentUpdated.viewLink")} `,
          createElement("a", { href: planResourcesUrl }, newParentName),
          ` ${t("visualization.toast.parentUpdated.planResources")}`
        )

        toast.dismiss()

        toast.success(
          {
            title: mainMessage,
            body: bodyContent
          },
          {
            position: "bottom-center",
            duration: 8000
          }
        )
      }
    },
    onError: (error, { selectedNodeName }) => {
      // Invalidate cache to ensure UI reflects actual backend state
      void queryClient.invalidateQueries({ queryKey: ["relatedCustomers"] })
      void queryClient.invalidateQueries({ queryKey: ["customerNeighbors"] })

      const errorTitle = t("visualization.toast.parentUpdateFailed.title", { customerName: selectedNodeName })

      let errorMessage: string

      if (error instanceof AuthorizationError) {
        errorMessage = t("visualization.toast.parentUpdateFailed.forbidden")
      } else {
        const errorPrefix = t("visualization.toast.parentUpdateFailed.errorPrefix")
        const unknownErrorText = t("visualization.error.unknown")
        errorMessage = error instanceof Error ? `${errorPrefix} ${error.message}` : `${errorPrefix} ${unknownErrorText}`
      }

      toast.error(
        {
          title: errorTitle,
          body: errorMessage
        },
        {
          position: "bottom-center",
          duration: 8000
        }
      )
    }
  })

  return {
    updateParentEdgeAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    reset: mutation.reset
  }
}
